import { z } from "zod";

import type { JsonObject } from "../types";

import { coerceToJsonObject } from "../json";
import { jsonValueSchema } from "../schemas/jsonValue";
import { planRevisionRepoSelectionResultSchema } from "./repoSelectionTransport";
import {
  planRevisionContextSchema,
  planRevisionPlanPipelineSchema,
} from "./schemas";

const phaseBudgetOverrideMapSchema = z.record(
  z.string(),
  z.number().int().positive().nullable(),
);

const phaseBudgetOverridesByModeSchema = z.record(
  z.string(),
  phaseBudgetOverrideMapSchema,
);

export const planRevisionPlanPipelineDispatchEnvelopeSchema = z
  .object({
    args: z.array(z.string()).optional(),
    context: z.record(z.string(), jsonValueSchema),
    expected_response_schema: z.record(z.string(), jsonValueSchema).optional(),
    expected_response_schema_name: z.string().trim().min(1).optional(),
    mode: z.literal("execute"),
    parent_result: planRevisionRepoSelectionResultSchema.optional(),
    phase_budget_overrides: phaseBudgetOverrideMapSchema.optional(),
    phase_budget_overrides_by_mode: phaseBudgetOverridesByModeSchema.optional(),
    plan_revision_context: planRevisionContextSchema,
    prompt: z.string(),
    provider: z.string().trim().min(1).optional(),
    skip_source_control: z.boolean().optional(),
    source_control_repository_allowlist: z.array(z.string()).optional(),
  })
  .strict();

export type PlanRevisionPlanPipelineDispatchEnvelope = z.infer<
  typeof planRevisionPlanPipelineDispatchEnvelopeSchema
>;

export const planRevisionPlanPipelineCompletionEnvelopeSchema = z
  .object({ parsed_result: planRevisionPlanPipelineSchema.nullable() })
  .strict();

export type PlanRevisionPlanPipelineCompletionEnvelope = z.infer<
  typeof planRevisionPlanPipelineCompletionEnvelopeSchema
>;

export function parsePlanRevisionPlanPipelineDispatchEnvelope(
  value: unknown,
): PlanRevisionPlanPipelineDispatchEnvelope | null {
  const parsed =
    planRevisionPlanPipelineDispatchEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializePlanRevisionPlanPipelineDispatchEnvelope(
  envelope: PlanRevisionPlanPipelineDispatchEnvelope,
): JsonObject {
  return coerceToJsonObject(
    planRevisionPlanPipelineDispatchEnvelopeSchema.parse(envelope),
  );
}

export function parsePlanRevisionPlanPipelineCompletionEnvelope(
  value: unknown,
): PlanRevisionPlanPipelineCompletionEnvelope | null {
  const parsed =
    planRevisionPlanPipelineCompletionEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializePlanRevisionPlanPipelineCompletionEnvelope(
  envelope: PlanRevisionPlanPipelineCompletionEnvelope,
): JsonObject {
  return coerceToJsonObject(
    planRevisionPlanPipelineCompletionEnvelopeSchema.parse(envelope),
  );
}

export function extractPlanRevisionPlanPipelineResult(
  value: unknown,
): z.infer<typeof planRevisionPlanPipelineSchema> | null {
  const completionEnvelope =
    parsePlanRevisionPlanPipelineCompletionEnvelope(value);
  if (completionEnvelope) {
    return completionEnvelope.parsed_result;
  }

  const directResult = planRevisionPlanPipelineSchema.safeParse(value);
  if (directResult.success) {
    return directResult.data;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    const parsedJson: unknown = JSON.parse(value);
    const parsedResult = planRevisionPlanPipelineSchema.safeParse(parsedJson);
    return parsedResult.success ? parsedResult.data : null;
  } catch {
    return null;
  }
}
